import { TitleButton } from '@/common/buttons/TitleButton';
import { KrCard } from '@/common/card/KrCard';
import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { PieChart } from '@/common/dashboard/charts/PieChart';
import { Spacer } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { useDispReqParam } from './useDispReqParam';

const compactCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});
const pct = (val) => `${(val * 100).toFixed(1)}%`;

// Slices below this share of the donut total fold into Other
const MIN_SLICE_SHARE = 0.02;
// Codes beyond this many (by resolved amount) fold into Other on the outcome chart
const MAX_OUTCOME_CODES = 5;

const OUTCOME_SUM_KEYS = [
  'accepted',
  'rejectedPaid',
  'rejectedDue',
  'resolved',
  'outstanding',
  'created',
  'uraRevisionAmt',
];

export const McdDispCodeOutcomeCards = ({ dispCodeCount }) => {
  const rsrc = useRsrc();
  const theme = useTheme();
  const [rows, setRows] = useState();
  const [mixMeasure, setMixMeasure] = useState('createdAmt');
  const [outcomeAsPct, setOutcomeAsPct] = useState(true);

  const reqParam = useDispReqParam(dispCodeCount);

  useEffect(() => {
    let stale = false;
    rsrc.data('sc', 'MCD_DISP_CODE_OUTCOME_DATA_PLUGIN', reqParam).then((res) => {
      if (!stale) setRows(res);
    });
    return () => {
      stale = true;
    };
  }, [reqParam]);

  const codeRows = useMemo(
    () =>
      (rows ?? []).map((row) => {
        const accepted = Number(row.acceptedAmt ?? 0);
        const rejectedPaid = Number(row.rejectedPaidAmt ?? 0);
        const rejectedDue = Number(row.rejectedDueAmt ?? 0);
        return {
          ...row,
          // Code pairs have no enum color, so they take Highcharts' palette; only the catch-all buckets are grey
          color: row.color ?? (['Multiple', 'Uncoded'].includes(row.code) ? theme.palette.grey[500] : undefined),
          created: Number(row.createdAmt ?? 0),
          outstanding: Number(row.outstandingAmt ?? 0),
          accepted,
          rejectedPaid,
          rejectedDue,
          resolved: accepted + rejectedPaid + rejectedDue,
          acceptRate:
            accepted + rejectedPaid + rejectedDue > 0 ? accepted / (accepted + rejectedPaid + rejectedDue) : null,
        };
      }),
    [rows, theme],
  );

  if (!rows) return null;

  return (
    <Spacer vertical weights={[1, 1]}>
      <DispCodeMixCard codeRows={codeRows} measure={mixMeasure} setMeasure={setMixMeasure} />
      <DispCodeOutcomeCard codeRows={codeRows} asPct={outcomeAsPct} setAsPct={setOutcomeAsPct} />
    </Spacer>
  );
};

const DispCodeMixCard = ({ codeRows, measure, setMeasure }) => {
  const theme = useTheme();
  const isCreated = measure === 'createdAmt';
  const valueKey = isCreated ? 'created' : 'outstanding';
  const total = codeRows.reduce((sum, row) => sum + row[valueKey], 0);

  const slices = [];
  const otherRows = [];
  for (const row of [...codeRows].sort((a, b) => b[valueKey] - a[valueKey])) {
    if (row[valueKey] <= 0) continue;
    if (total > 0 && row[valueKey] / total < MIN_SLICE_SHARE) otherRows.push(row);
    else slices.push(row);
  }

  const tooltipHtml = (row) =>
    `${row.displayName}<br/>${compactCurrency.format(row[valueKey])} (${pct(row[valueKey] / total)})`;

  const data = slices.map((row) => ({
    label: row.code,
    value: row[valueKey],
    color: row.color,
    tooltipHtml: tooltipHtml(row),
  }));
  if (otherRows.length) {
    const otherVal = otherRows.reduce((sum, row) => sum + row[valueKey], 0);
    data.push({
      label: 'Other',
      value: otherVal,
      color: theme.palette.grey[400],
      tooltipHtml: otherRows.map((row) => `${row.code}: ${compactCurrency.format(row[valueKey])}`).join('<br/>'),
    });
  }

  return (
    <KrCard
      title={isCreated ? 'Disputes Created by Code' : 'Disputes Outstanding by Code'}
      buttons={
        <TitleButton
          icon={far.faCircleDollar}
          toolTip={isCreated ? 'Show Outstanding' : 'Show Created'}
          onClick={() => setMeasure(isCreated ? 'outstandingAmt' : 'createdAmt')}
        />
      }
    >
      <PieChart
        data={data}
        legend="bottom"
        centerLabel={compactCurrency.format(total)}
        centerSubLabel={isCreated ? 'Created' : 'Outstanding'}
      />
    </KrCard>
  );
};

const DispCodeOutcomeCard = ({ codeRows, asPct, setAsPct }) => {
  const theme = useTheme();

  // Largest resolved amounts first; Other always last
  const byResolved = codeRows.filter((row) => row.resolved > 0).sort((a, b) => b.resolved - a.resolved);
  const otherRows = byResolved.slice(MAX_OUTCOME_CODES);
  const sorted = byResolved.slice(0, MAX_OUTCOME_CODES);
  if (otherRows.length) {
    const other = Object.fromEntries(
      OUTCOME_SUM_KEYS.map((key) => [key, otherRows.reduce((sum, row) => sum + Number(row[key] ?? 0), 0)]),
    );
    sorted.push({
      ...other,
      code: 'Other',
      displayName: `${otherRows.length} codes: ${otherRows
        .slice(0, 8)
        .map((row) => row.code)
        .join(', ')}${otherRows.length > 8 ? ', …' : ''}`,
      acceptRate: other.resolved > 0 ? other.accepted / other.resolved : null,
    });
  }

  const rowByCategory = Object.fromEntries(sorted.map((row) => [row.code, row]));
  const categories = Object.keys(rowByCategory);
  const val = (row, key) => (asPct ? (row[key] / row.resolved) * 100 : row[key]);

  const data = {
    categories,
    series: [
      { label: 'Accepted', values: sorted.map((row) => val(row, 'accepted')), color: theme.palette.success.main },
      {
        label: 'Rejected (Paid)',
        values: sorted.map((row) => val(row, 'rejectedPaid')),
        color: theme.palette.error.main,
      },
      {
        label: 'Rejected (Due)',
        values: sorted.map((row) => val(row, 'rejectedDue')),
        color: theme.palette.warning.main,
      },
    ],
  };

  const tooltipFormatter = ({ category }) => {
    const row = rowByCategory[category];
    if (!row) return category;
    return [
      `<b>${row.code}</b> - ${row.displayName}`,
      `Accepted: ${compactCurrency.format(row.accepted)}`,
      `Rejected (Paid): ${compactCurrency.format(row.rejectedPaid)}`,
      `Rejected (Due): ${compactCurrency.format(row.rejectedDue)}`,
      `URA Revisions: ${compactCurrency.format(Number(row.uraRevisionAmt ?? 0))}`,
      `Acceptance Rate: ${row.acceptRate == null ? 'n/a' : pct(row.acceptRate)}`,
      `Outstanding: ${compactCurrency.format(row.outstanding)}`,
      `Created: ${compactCurrency.format(row.created)}`,
    ].join('<br/>');
  };

  return (
    <KrCard
      title="Dispute Outcome by Code"
      buttons={
        <TitleButton
          icon={asPct ? far.faDollarSign : far.faPercent}
          toolTip={asPct ? 'Show Dollars' : 'Show Percent of Resolved'}
          onClick={() => setAsPct(!asPct)}
        />
      }
    >
      <CartesianChart
        data={data}
        horizontal
        stacked
        legend="top"
        yLabel={asPct ? 'Share of Resolved (%)' : 'Resolved Amount ($)'}
        xTickFormatter={(category) => {
          const row = rowByCategory[category];
          return row ? `${category} (${compactCurrency.format(row.outstanding)} open)` : category;
        }}
        tooltipFormatter={tooltipFormatter}
      />
    </KrCard>
  );
};
